import { router } from "@inertiajs/react";
import {
  Text,
  Box,
  Flex,
  DropdownMenu,
  Button,
  Card,
  Grid,
  Heading,
} from "@radix-ui/themes";
import { MouseEvent } from "react";
import { Pencil } from "lucide-react";
import { Application, CarCardCar, Participant } from "@/types/shared";
import UserPageShell from "@/shared/users/UserPageShell";
import GarageGrid from "@/shared/cars/GarageGrid";

interface User {
  id: number;
  email: string;
}

interface ProfilePaths {
  new_media_group_participant: string;
  new_driver_car: string;
  new_team_participant: string;
  new_staff_participant: string;
  events: string;
}

interface ProfileProps {
  user: User;
  participants: Participant[];
  applications: Application[];
  driver_cars: CarCardCar[];
  has_staff_participant: boolean;
  has_media_group_participant: boolean;
  has_team_participant: boolean;
  paths: ProfilePaths;
}

export default function Profile({
  participants,
  driver_cars,
  has_staff_participant,
  has_media_group_participant,
  has_team_participant,
  paths,
}: ProfileProps) {
  const handleNavigate = (
    { target }: MouseEvent<HTMLElement>,
    href: string,
  ) => {
    if ((target as HTMLElement).dataset.disabled == undefined)
      router.visit(href);
  };

  const editLinks: { [key: string]: string } = {
    Staff: "/staff_participant/edit",
    "Media Group": "/media_group_participant/edit",
  };

  function getEditLink(participant: Participant): string {
    if (participant.participantable_type === "Team") {
      return participant.is_owner ? "/my-team" : `/teams/${participant.slug}`;
    }
    return editLinks[participant.type] ?? "#";
  }

  return (
    <UserPageShell title="User Profile">
      <section>
        <Flex justify="between" mb="4">
          <Heading>Ways You Participate</Heading>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button variant="solid">
                Create...
                <DropdownMenu.TriggerIcon />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <DropdownMenu.Item onClick={(e) => handleNavigate(e, paths.new_driver_car)}>
                Car
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={(e) => handleNavigate(e, paths.new_team_participant)}
                disabled={has_team_participant}
              >
                Team
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={(e) => handleNavigate(e, paths.new_media_group_participant)}
                disabled={has_media_group_participant}
              >
                Media
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={(e) => handleNavigate(e, paths.new_staff_participant)}
                disabled={has_staff_participant}
              >
                Staff
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Flex>

        {participants.length === 0 ? (
          <Box>
            <Text as="p" size="3" mb="4">
              How do <b>you</b> like to participate at drift events? You'll need a participant profile for the kind of attendance you want before you can apply to events.
            </Text>
            <Flex direction="column" gap="3">
              <Text as="p" size="3">
                Drone pilot, videographer, photographer, or a mix of all of the above? You want to{" "}
                <a href={paths.new_media_group_participant} className="text-accent9 hover:underline">
                  create a media group
                </a>
                .
              </Text>
              <Text as="p" size="3">
                Behind the wheel? You want to{" "}
                <a href={paths.new_driver_car} className="text-accent9 hover:underline">
                  add a driver and car combo
                </a>
                .
              </Text>
              <Text as="p" size="3">
                Running with a team? You want to{" "}
                <a href={paths.new_team_participant} className="text-accent9 hover:underline">
                  create a team
                </a>{" "}
                and invite your teammates.
              </Text>
              <Text as="p" size="3">
                Helping the event run? You want to{" "}
                <a href={paths.new_staff_participant} className="text-accent9 hover:underline">
                  set up a staff profile
                </a>
                .
              </Text>
              <Text as="p" size="3">
                Just want to watch? No profile necessary - go find an{" "}
                <a href={paths.events} className="text-accent9 hover:underline">
                  event
                </a>
                {" "}and we'll see you there!
              </Text>
            </Flex>
          </Box>
        ) : (
          <Grid columns={{ initial: "1", sm: "2", md: "3" }} gap="3">
            {participants.map((participant) => (
              <Box key={participant.id}>
                <Card>
                  <Flex justify="between">
                    <Box>
                      <Text as="div" size="2" weight="bold">
                        {participant.name}
                      </Text>
                      <Text as="div" size="1" color="gray">
                        {participant.type}
                      </Text>
                    </Box>

                    <Flex gap="3" align="center">
                      <Button
                        variant="soft"
                        color="gray"
                        onClick={(e) => handleNavigate(e, getEditLink(participant))}
                      >
                        <Pencil />
                      </Button>
                    </Flex>
                  </Flex>
                </Card>
              </Box>
            ))}
          </Grid>
        )}
      </section>

      <section>
        <Heading mb="4">Garage</Heading>
        {driver_cars.length > 0 ? (
          <GarageGrid cars={driver_cars} />
        ) : (
          <p>No cars in your garage found. Get started above.</p>
        )}
      </section>
    </UserPageShell>
  );
}
